<svelte:options runes={false} />
<script>
  // The invariant-checks strip (TASK-K2T-8): green-or-loud. One card per check in server render
  // order — status pill (label + color, never color alone), one-line verdict, and the offending
  // symbols/ids behind a disclosure when the check found any. N/A is an honest state (the strategy
  // shape cannot produce the inputs), rendered muted — never as a fake green.
  import { statusLabel, statusTone } from '../../../lib/integrity.js';

  export let checks = [];

  const NAMES = {
    NAV_RECOMPUTE: 'NAV recompute',
    BOOK_RECON: 'Book recon',
    LEDGER_COHERENCE: 'Ledger coherence',
    MARK_COVERAGE: 'Mark coverage',
    BAR_FRESHNESS: 'Bar freshness',
    CADENCE: 'Cadence',
    ORDER_HYGIENE: 'Order hygiene',
    CAP_AGGREGATE: 'Cap aggregate',
  };
  const name = (key) => NAMES[key] ?? key;
</script>

<ul class="it-checks" aria-label="Invariant checks">
  {#each checks as c (c.key)}
    <li class="it-check it-check-{statusTone(c.status)}">
      <div class="it-check-head">
        <span class="it-check-name" title={c.key}>{name(c.key)}</span>
        <span class="it-pill it-pill-{statusTone(c.status)}">{statusLabel(c.status)}</span>
      </div>
      <span class="it-check-detail">{c.detail || '—'}</span>
      {#if (c.evidence ?? []).length > 0}
        <details class="it-check-evidence">
          <summary>{c.evidence.length} finding{c.evidence.length === 1 ? '' : 's'}</summary>
          <ul>
            {#each c.evidence as e, i (i)}<li class="num">{e}</li>{/each}
          </ul>
        </details>
      {/if}
    </li>
  {/each}
</ul>

<style>
  .it-checks {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 0.5rem;
    list-style: none; margin: 0; padding: 0;
  }
  .it-check {
    display: flex; flex-direction: column; gap: 0.2rem;
    border: 1px solid var(--border-subtle, #1e1f22); border-left-width: 3px;
    border-radius: 6px; padding: 0.5rem 0.6rem;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  .it-check-ok { border-left-color: var(--success, #1aa260); }
  .it-check-warn { border-left-color: var(--warning, #d8a000); }
  .it-check-error { border-left-color: var(--danger, #e0533a); }
  .it-check-na { border-left-color: var(--border-subtle, #1e1f22); opacity: 0.75; }
  .it-check-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
  .it-check-name { font-size: 0.72rem; font-weight: 600; color: var(--text-primary, #e8e9ea); }
  .it-pill {
    font-size: 0.58rem; font-weight: 700; letter-spacing: 0.06em;
    padding: 0.1rem 0.35rem; border-radius: 3px; white-space: nowrap;
  }
  .it-pill-ok { background: rgba(26, 162, 96, 0.15); color: var(--success, #1aa260); }
  .it-pill-warn { background: rgba(216, 160, 0, 0.15); color: var(--warning, #d8a000); }
  .it-pill-error { background: rgba(224, 83, 58, 0.18); color: var(--danger, #e0533a); }
  .it-pill-na { background: var(--surface-2, rgba(255, 255, 255, 0.05)); color: var(--text-muted, #8a8b8d); }
  .it-check-detail { font-size: 0.68rem; color: var(--text-muted, #8a8b8d); }
  .it-check-evidence summary { cursor: pointer; font-size: 0.66rem; color: var(--text-secondary, #b5b6b8); }
  .it-check-evidence ul { margin: 0.3rem 0 0; padding-left: 1rem; max-height: 9rem; overflow: auto; }
  .it-check-evidence li { font-size: 0.66rem; color: var(--text-muted, #8a8b8d); }
  .num { font-variant-numeric: tabular-nums; }
</style>
